import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import type { ExtensionDefinition } from '@backstage/frontend-plugin-api';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { SidebarLogo } from './SidebarLogo';
import { SearchPalette } from './SearchPalette';
import MenuIcon from '@material-ui/icons/Menu';
import SettingsIcon from '@material-ui/icons/Settings';
import ChatIcon from '@material-ui/icons/Chat';
import ExtensionIcon from '@material-ui/icons/Extension';
import StorageIcon from '@material-ui/icons/Storage';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import DescriptionIcon from '@material-ui/icons/Description';
import GroupIcon from '@material-ui/icons/Group';
import PersonIcon from '@material-ui/icons/Person';
import DnsIcon from '@material-ui/icons/Dns';
import PublicIcon from '@material-ui/icons/Public';
import CategoryIcon from '@material-ui/icons/Category';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import TuneIcon from '@material-ui/icons/Tune';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { usePermission } from '@backstage/plugin-permission-react';
import { abbyMemoryReadPermission } from '@abstract/plugin-abby-memory-common';
import MemoryIcon from '@material-ui/icons/Memory';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import ApiIcon from '@material-ui/icons/SettingsInputComponent';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import SecurityIcon from '@material-ui/icons/Security';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { useIsRichardAdminAuthorized } from '../richardAdmin';

const useExternalSubmenuStyles = makeStyles(theme => ({
  link: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: 48,
    boxSizing: 'border-box',
    padding: theme.spacing(2.5),
    color: theme.palette.navigation.color,
    textDecoration: 'none',
    '&:hover': {
      background:
        theme.palette.navigation.navItem?.hoverBackground || '#6f6f6f',
      color: theme.palette.navigation.selectedColor,
      textDecoration: 'none',
    },
  },
  label: {
    margin: theme.spacing(1.75),
    marginLeft: theme.spacing(1),
    fontSize: theme.typography.body2.fontSize,
    whiteSpace: 'nowrap',
  },
  externalIcon: {
    marginLeft: 'auto',
  },
}));

function OpenWebUISubmenuItem() {
  const classes = useExternalSubmenuStyles();
  // Centralized in app-config (chatUi.baseUrl), not hardcoded here.
  const configApi = useApi(configApiRef);
  const chatUrl = configApi.getOptionalString('chatUi.baseUrl') ?? '';

  return (
    <a
      className={classes.link}
      href={chatUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="OpenWebUI (si apre in una nuova scheda)"
    >
      <ChatIcon fontSize="small" />
      <Typography component="span" className={classes.label}>
        OpenWebUI
      </Typography>
      <OpenInNewIcon fontSize="small" className={classes.externalIcon} />
    </a>
  );
}

const catalogKinds = [
  { kind: 'component', title: 'Components', icon: ExtensionIcon },
  { kind: 'api', title: 'APIs', icon: StorageIcon },
  { kind: 'template', title: 'Templates', icon: DescriptionIcon },
  { kind: 'group', title: 'Groups', icon: GroupIcon },
  { kind: 'user', title: 'Users', icon: PersonIcon },
  { kind: 'system', title: 'Systems', icon: DnsIcon },
  { kind: 'domain', title: 'Domains', icon: PublicIcon },
];

// Gated by the host authorization hook; lives in the "More" submenu.
function RichardAdminSubmenuItem() {
  const { allowed } = useIsRichardAdminAuthorized();
  return allowed ? (
    <SidebarSubmenuItem icon={TuneIcon} to="/richard" title="Richard Admin" />
  ) : null;
}

// Visible only to holders of abby-memory.read (RBAC). UX only: the backend enforces it.
function AbbyMemorySidebarItem() {
  const { allowed } = usePermission({ permission: abbyMemoryReadPermission });
  return allowed ? (
    <SidebarItem icon={MemoryIcon} to="/abby-memory" text="Abby Memories" />
  ) : null;
}

export const SidebarContent: ExtensionDefinition<any> =
  NavContentBlueprint.make({
    params: {
      component: ({ navItems }) => {
        const nav = navItems.withComponent(item => (
          <SidebarItem
            icon={() => item.icon}
            to={item.href}
            text={item.title}
          />
        ));

        return (
          <Sidebar>
            <SidebarLogo />
            <SearchPalette />
            <SidebarDivider />
            <SidebarGroup label="Menu" icon={<MenuIcon />}>
              {void nav.take('page:catalog')}
              {nav.take('page:scaffolder')}
              <SidebarItem icon={CategoryIcon} to="/catalog" text="Catalog">
                <SidebarSubmenu title="Catalog">
                  {catalogKinds.map(({ kind, title, icon }) => (
                    <SidebarSubmenuItem
                      key={kind}
                      icon={icon}
                      to={`/catalog?filters[kind]=${kind}`}
                      title={title}
                    />
                  ))}
                </SidebarSubmenu>
              </SidebarItem>
              {/* Keys stay easy to reach; chat and agent experiences live under
                  AI Tools alongside the external OpenWebUI link. */}
              <SidebarItem icon={VpnKeyIcon} to="/litellm" text="AI Keys" />
              <SidebarItem icon={ExtensionIcon} text="AI Tools">
                <SidebarSubmenu title="AI Tools">
                  <SidebarSubmenuItem
                    icon={ChatIcon}
                    to="/ai-conversation"
                    title="AI Assistant (RAG)"
                  />
                  <SidebarSubmenuItem
                    icon={ExtensionIcon}
                    to="/ai-agents"
                    title="AI Agents"
                  />
                  <OpenWebUISubmenuItem />
                </SidebarSubmenu>
              </SidebarItem>
              <AbbyMemorySidebarItem />
              <SidebarDivider />
              <SidebarItem
                icon={AnnouncementIcon}
                to="/announcements"
                text="Announcements"
              />
              <SidebarItem icon={ApiIcon} to="/api-docs" text="APIs" />
              <SidebarItem icon={MenuBookIcon} to="/docs" text="Docs" />
              {/* Tech Insights: its page declares no routeRef, so AppNav skips it
                  and it can never appear via nav.rest(); link it explicitly.
                  Richard Admin is gated by the host authorization hook. */}
              <SidebarItem icon={MoreHorizIcon} text="More">
                <SidebarSubmenu title="More">
                  <RichardAdminSubmenuItem />
                  <SidebarSubmenuItem
                    icon={AccountTreeIcon}
                    to="/catalog-graph"
                    title="Catalog Graph"
                  />
                  <SidebarSubmenuItem
                    icon={SecurityIcon}
                    to="/rbac"
                    title="RBAC"
                  />
                  <SidebarSubmenuItem
                    icon={AddCircleOutlineIcon}
                    to="/catalog-import"
                    title="Register Existing"
                  />
                  <SidebarSubmenuItem
                    icon={EmojiObjectsIcon}
                    to="/tech-insights"
                    title="Tech Insights"
                  />
                </SidebarSubmenu>
              </SidebarItem>
              {/* These pages are rendered explicitly above (top-level AI Keys,
                  AI tools submenu, search palette). AppNav auto-discovers a nav
                  item from every page extension that has a title, an icon AND a
                  routeRef, so anything not taken here would fall into
                  nav.rest() below and render a second time — the duplication the
                  notifications comment describes. Of the takes below only
                  page:search actually carries a routeRef
                  today; the litellm/ai-agents pages declare none, so their takes
                  are defensive against those plugins adding one later. */}
              {void nav.take('page:litellm')}
              {void nav.take('page:ai-conversation')}
              {void nav.take('page:ai-conversation/analytics')}
              {void nav.take('page:ai-agents')}
              {void nav.take('page:richard-admin')}
              {void nav.take('page:abby-memory')}
              {void nav.take('page:search')}
              {void nav.take('page:announcements')}
              {void nav.take('page:api-docs')}
              {void nav.take('page:techdocs')}
              {void nav.take('page:catalog-graph')}
              {void nav.take('page:catalog-import')}
              {void nav.take('page:rbac')}
              {void nav.take('page:tech-insights')}
              {void nav.take('page:home')}
              {/* The notifications plugin only registers a page, not a nav
                  item (upstream TODO) — without this it falls into
                  nav.rest() below AND we render NotificationsSidebarItem
                  (badge-enabled) near the bottom, showing "Notifications"
                  twice. */}
              {void nav.take('page:notifications')}
              <SidebarDivider />
              <SidebarScrollWrapper>
                {nav.rest({ sortBy: 'title' })}
              </SidebarScrollWrapper>
            </SidebarGroup>
            <SidebarSpace />
            <SidebarDivider />
            <NotificationsSidebarItem />
            <SidebarDivider />
            <SidebarGroup
              label="Settings"
              icon={<SettingsIcon />}
              to="/settings"
            >
              {nav.take('page:user-settings')}
            </SidebarGroup>
          </Sidebar>
        );
      },
    },
  });
